import { Fragment, useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import moment from 'moment';
import { Heading } from '@lendi-ui/typography';

import { BUSINESS_FULL_DOC_INCOME_ITEMS, FIELD_PATHS } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { BusinessIncomeDetails, IncomeEntityIndex } from '@funnels/types';
import { Accordion, AccordionGroup, Divider, FunnelGrid, FunnelGridUnit } from '@funnels/ui-components/Common';
import DropDownField from '@funnels/ui-components/Fields/_uncontrolled/DropdownField';
import { BusinessFullDocIncomeItem } from '@funnels/ui-components/Income';

export const getFinancialYearOptions = (currentFinancialYear: number, apidata: BusinessIncomeDetails[]) => {
  const additionalYears = apidata
    .map((businessIncomeDetails) => businessIncomeDetails.year)
    .filter((year, idx, arr): year is number => {
      return !!year && typeof year === 'number' && (!arr.includes(year! + 1) || arr.includes(year! - 1));
    });
  const values = new Set([currentFinancialYear, currentFinancialYear - 1, ...additionalYears]);
  return Array.from(values)
    .sort()
    .reverse()
    .map((year) => ({ label: `FY${year} & FY${year - 1}`, value: `${year}` }));
};

type ItemRow = {
  type: 'item';
  item: keyof typeof BUSINESS_FULL_DOC_INCOME_ITEMS;
};

type HeadingRow = {
  type: 'heading';
  heading: string;
};

type RowConfig = ItemRow | HeadingRow;

const ROW_CONFIGS: RowConfig[] = [
  { type: 'item', item: 'profit' },
  { type: 'heading', heading: 'Addback' },
  { type: 'item', item: 'depreciation' },
  { type: 'item', item: 'salary' },
  { type: 'item', item: 'expenses' },
  { type: 'item', item: 'superannuation' },
  { type: 'item', item: 'lease' },
  { type: 'item', item: 'interest' },
  { type: 'item', item: 'other' },
  { type: 'heading', heading: 'Deduction' },
  { type: 'item', item: 'deduction' },
];

interface BusinessFullDocIncomeProps extends IncomeEntityIndex {}

export const BusinessFullDocIncome = (props: BusinessFullDocIncomeProps) => {
  const { incomeIndex } = props;
  const [formValues, setFormValue] = useFormValues();
  const now = moment();
  const currentFinancialYear = now.quarter() <= 2 ? now.year() - 1 : now.year();
  const [financialYear, setFinancialYear] = useState(currentFinancialYear);
  const businessIncomes: BusinessIncomeDetails[] = get(
    formValues,
    FIELD_PATHS.INCOME_BUSINESS_INCOMES(incomeIndex),
    []
  );

  const currYearIncomeIndex = businessIncomes.findIndex((income) => income.year === financialYear);
  const prevYearIncomeIndex = businessIncomes.findIndex((income) => income.year === financialYear - 1);

  const finyearOptions = getFinancialYearOptions(currentFinancialYear, businessIncomes);
  useEffect(() => {
    if (currYearIncomeIndex === -1) {
      setFormValue(FIELD_PATHS.INCOME_BUSINESS_INCOME(incomeIndex, businessIncomes.length), { year: financialYear });
    }
    if (prevYearIncomeIndex === -1) {
      setFormValue(
        FIELD_PATHS.INCOME_BUSINESS_INCOME(incomeIndex, businessIncomes.length + Number(currYearIncomeIndex === -1)),
        { year: financialYear - 1 }
      );
    }
  }, [currYearIncomeIndex, prevYearIncomeIndex, businessIncomes.length, financialYear, setFormValue, incomeIndex]);

  return (
    <>
      <FunnelGridUnit mb="md">
        <DropDownField
          label="Select the 2 most recent financial years"
          subLabel="If you are not entering the most recent financial years' data, some lenders may not accept the oldest year"
          value={financialYear.toString()}
          options={finyearOptions}
          onChange={(value) => setFinancialYear(Number(value))}
        />
      </FunnelGridUnit>
      <FunnelGridUnit>
        <AccordionGroup>
          <Accordion headerText={`Taxable income for FY${financialYear}`}>
            <FunnelGrid>
              {ROW_CONFIGS.map((row) => {
                if (row.type === 'heading') {
                  return (
                    <Fragment key={`${financialYear} - ${row.heading}`}>
                      <FunnelGridUnit my="sm">
                        <Divider />
                      </FunnelGridUnit>
                      <FunnelGridUnit my="sm">
                        <Heading size="xs">{row.heading}</Heading>
                      </FunnelGridUnit>
                    </Fragment>
                  );
                } else {
                  return (
                    <FunnelGridUnit my="sm" key={`${financialYear} - ${row.item}`}>
                      <BusinessFullDocIncomeItem
                        hasLabel
                        incomeIndex={incomeIndex}
                        businessIncomeIndex={currYearIncomeIndex}
                        item={row.item}
                        isOptional
                        disableFailMessage
                      />
                    </FunnelGridUnit>
                  );
                }
              })}
            </FunnelGrid>
          </Accordion>
          <Accordion headerText={`Taxable income for FY${financialYear - 1}`}>
            <FunnelGrid>
              {ROW_CONFIGS.map((row) => {
                if (row.type === 'heading') {
                  return (
                    <Fragment key={`${financialYear - 1} - ${row.heading}`}>
                      <FunnelGridUnit mb="md">
                        <Divider />
                      </FunnelGridUnit>
                      <FunnelGridUnit mb="md">
                        <Heading size="xs">{row.heading}</Heading>
                      </FunnelGridUnit>
                    </Fragment>
                  );
                } else {
                  return (
                    <FunnelGridUnit mb="md" key={`${financialYear - 1} - ${row.item}`}>
                      <BusinessFullDocIncomeItem
                        hasLabel
                        incomeIndex={incomeIndex}
                        businessIncomeIndex={prevYearIncomeIndex}
                        item={row.item}
                        isOptional
                        disableFailMessage
                      />
                    </FunnelGridUnit>
                  );
                }
              })}
            </FunnelGrid>
          </Accordion>
        </AccordionGroup>
      </FunnelGridUnit>
    </>
  );
};
